import { useEffect, useState } from "react";
import { PowerOff, Timer } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { maintenanceModeApi, type MaintenanceModeStatus } from "@/lib/maintenanceMode.api";
import { LoadingLine } from "../components/LoadingLine";
import { getApiErrorMessage } from "../settingsPage.helpers";

const DEFAULT_DURATION_MINUTES = 60;

// Starts/ends the settings-driven maintenance-mode session (C3). Starting it
// is super_admin-only — a production-affecting decision, even though the
// dev role is who acts during the session itself once it's open.
export function MaintenanceModePanel() {
  const [status, setStatus] = useState<MaintenanceModeStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [duration, setDuration] = useState(DEFAULT_DURATION_MINUTES);
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const response = await maintenanceModeApi.status();
      setStatus(response.data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Impossible de charger le mode maintenance."));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function handleStart() {
    setBusy(true);
    try {
      const response = await maintenanceModeApi.start(duration);
      setStatus(response.data);
      toast.success("Mode maintenance démarré.");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Impossible de démarrer le mode maintenance."));
    } finally {
      setBusy(false);
    }
  }

  async function handleEnd() {
    setBusy(true);
    try {
      const response = await maintenanceModeApi.end();
      setStatus(response.data);
      toast.success("Mode maintenance terminé.");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Impossible de terminer le mode maintenance."));
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <LoadingLine label="Chargement du mode maintenance..." />;

  const active = Boolean(status?.enabled);

  return (
    <section className="border border-border bg-card">
      <div className="flex items-center gap-2 border-b border-border px-4 py-3">
        <Timer size={15} className="text-brand-gold-dark" />
        <div>
          <h3 className="text-sm font-semibold text-foreground">Mode Maintenance</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Ouvre une fenêtre temporaire pour le compte dev/supervision — jamais les données
            réelles, toujours des données de test identifiables et nettoyables.
          </p>
        </div>
      </div>

      <div className="p-4">
        {active ? (
          <div className="flex flex-wrap items-center justify-between gap-3 border border-warning-border bg-warning-bg px-3 py-2.5">
            <span className="text-xs text-warning-text">
              Session active — expire le{" "}
              {status?.expiresAt ? new Date(status.expiresAt).toLocaleString("fr-FR") : "—"}.
            </span>
            <Button size="sm" variant="destructive" onClick={handleEnd} disabled={busy}>
              <PowerOff size={13} />
              Terminer maintenant
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="mb-1 block text-[11px] font-medium text-body">
                Durée (15 à 480 minutes)
              </label>
              <Input
                type="number"
                min={15}
                max={480}
                value={duration}
                onChange={(event) => setDuration(Number(event.target.value))}
                className="w-32"
              />
            </div>
            <Button size="sm" onClick={handleStart} disabled={busy}>
              <Timer size={13} />
              Démarrer la session
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
